import { Alert, AlertContent, AlertText } from "@hortis/ui/alert";
import { Button } from "@hortis/ui/button";
import { Drawer, DrawerContent } from "@hortis/ui/drawer";
import { useMediaQuery } from "@hortis/ui/hooks/media-query";
import { AlertTriangle } from "@hortis/ui/icons";
import { Skeleton } from "@hortis/ui/skeleton";
import { useGetLocationQuery } from "generated/graphql";
import { useCallback, useState } from "react";
import type { MapPositioningLocation } from "./types";

interface EditLocationPolygonOverlayProps {
  editingLocation: MapPositioningLocation;
  cancelEdit: () => void;
  confirmEdit: () => Promise<void>;
}

const ErrorAlert = () => (
  <Alert
    color="warning"
    data-cy="location-details-alert"
    className="flex max-h-9 flex-row items-center px-6"
  >
    <AlertTriangle className="shrink-0" />
    <AlertContent>
      <AlertText>Something went wrong.</AlertText>
    </AlertContent>
  </Alert>
);

const EditLocationPolygonOverlay = ({
  editingLocation,
  cancelEdit,
  confirmEdit,
}: EditLocationPolygonOverlayProps) => {
  const isDesktop = useMediaQuery("sm");
  const [loading, setLoading] = useState(false);

  const [{ data, fetching, error }] = useGetLocationQuery({
    variables: {
      id: editingLocation.id,
    },
  });

  const location = data?.location;

  const showError = (!fetching && location == null) || error != null;
  const showLoading = fetching && location == null;

  const handleConfirmEdit = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setLoading(true);
      await confirmEdit();
      setLoading(false);
    },
    [confirmEdit],
  );

  const content = (
    <div
      data-cy="edit-location-geometry-overlay"
      className="flex flex-col gap-4 md:flex-row md:items-center md:gap-8"
    >
      {showError ? (
        <ErrorAlert />
      ) : showLoading ? (
        <Skeleton className="h-9 w-full" />
      ) : (
        <div className="flex w-full items-center gap-3 overflow-hidden">
          <div className="overflow-hidden">
            <p className="truncate break-all text-sm font-medium text-grey-700">
              {location?.name ?? "-"}
            </p>
            <p className="truncate text-sm font-normal text-grey-500">
              {location?.description ?? "-"}
            </p>
          </div>
        </div>
      )}
      <div className="flex max-h-9 gap-3">
        <Button
          onClick={cancelEdit}
          testId="cancel-update-location-polygon"
          size="sm"
          className="max-h-9 w-full"
        >
          Cancel
        </Button>

        <Button
          variant="primary"
          testId="confirm-update-location-polygon"
          size="sm"
          onClick={handleConfirmEdit}
          loading={loading}
          className="max-h-9 w-full"
          disabled={showError || showLoading}
        >
          Confirm
        </Button>
      </div>
    </div>
  );

  return isDesktop ? (
    <div className="pointer-events-auto z-50 min-w-[360px] max-w-[480px] self-center rounded-xl border border-grey-200 bg-white p-4 shadow-md animate-in fade-in">
      {content}
    </div>
  ) : (
    <Drawer open onClose={cancelEdit} modal={false}>
      <DrawerContent className="px-4 py-6 shadow-md-up">
        {content}
      </DrawerContent>
    </Drawer>
  );
};

export { EditLocationPolygonOverlay, ErrorAlert };
